import { format } from "date-fns"

// Pure helper for the chat-style message feed. Groups consecutive messages from the same author
// (within the same calendar day) into a single visual cluster so the avatar + name + role render
// once per cluster and the timestamp renders on the last message. Kept free of React so it can be
// unit-tested directly. Clustering is PRESENTATIONAL ONLY — every message stays in the list with
// its own id, so DOM-level assertions (data-cy per message) are unaffected.

/**
 * Annotates each message with its cluster boundaries. A cluster is a run of consecutive messages
 * that share an author and fall on the same calendar day.
 *
 * @param {Array<{ id?: string, authorUserId?: string, created?: string }>} messages
 * @returns {Array<{ message: object, isClusterStart: boolean, isClusterEnd: boolean }>}
 */
export function getMessageClusters(messages = []) {
    const list = messages ?? []
    return list.map(toClusterEntry)

    function toClusterEntry(message, index) {
        return {
            message,
            isClusterStart: isClusterStart(list, index),
            isClusterEnd: isClusterEnd(list, index),
        }
    }
}

function isClusterStart(list, index) {
    if (index === 0) return true
    return !isSameCluster(list[index - 1], list[index])
}

function isClusterEnd(list, index) {
    if (index === list.length - 1) return true
    return !isSameCluster(list[index], list[index + 1])
}

// Two adjacent messages belong to the same cluster when they share an author identity and land on
// the same calendar day. When an author id is absent we fall back to the display name so anonymous
// recipient runs still cluster, and to a per-message sentinel when neither is present (never cluster).
function isSameCluster(previous, current) {
    if (!previous || !current) return false
    if (getAuthorKey(previous) !== getAuthorKey(current)) return false
    return getDayKey(previous) === getDayKey(current)
}

function getAuthorKey(message) {
    if (message.authorUserId) return `id:${message.authorUserId}`
    if (message.authorName) return `name:${message.authorName}`
    return `ref:${message.id ?? Math.random()}`
}

function getDayKey(message) {
    const value = message.created ?? message.modified
    if (!value) return ""
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ""
    return format(date, "yyyy-MM-dd")
}
